import {
  CheckCircle2,
  Edit3,
  Eye,
  Hash,
  Image as ImageIcon,
  Link,
  Loader2,
  MessageSquare,
  Palette,
  Plus,
  Sparkles,
  Video,
  Wand2,
  X,
  Zap,
} from "lucide-react";
import React, { FormEvent, useEffect, useMemo, useState } from "react";
import { useActiveContext } from "../../contexts/ActiveContextContext";
import { useTranslation } from "../../contexts/LanguageContext";
import postsService from "../../services/postsService";
import { showConfirm, showToastError } from "../../shared/alertService";
import { getPlatformName } from "../../utils/platformIcons";
import {
  getStrictestCaptionLimit,
  POST_CAPTION_LIMITS,
} from "../../utils/postCaptionLimits";
import { getStrictestHashtagLimit } from "../../utils/postHashtagLimits";
import SocialVariationsModal from "./SocialVariationsModal";
import { cn } from "../../utils/cn";
import Button from "../ui/Button";
import CharCounter from "../ui/CharCounter";
import Input from "../ui/Input";
import Modal from "../ui/Modal";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/Tabs";
import Textarea from "../ui/Textarea";

interface EditPostModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => Promise<void>;
  loading: boolean;
  editContent: string;
  setEditContent: (content: string) => void;
  editHashtags: string;
  setEditHashtags: (hashtags: string) => void;
  editCallToAction: string;
  setEditCallToAction: (cta: string) => void;
  editCallToActionUrl: string;
  setEditCallToActionUrl: (url: string) => void;
  postId?: number | null;
  /** Plataforma do post sendo editado — usada no limite de caracteres e na pré-visualização. */
  platform?: string;
  // Features de IA (opcionais, para posts aprovados)
  onEnhanceText?: () => void;
  onGenerateImage?: () => void;
  onGenerateVideo?: () => void;
  onUploadImage?: () => void;
  onUploadVideo?: () => void;
  onEnhanceImage?: () => void;
  onVisualSuggestions?: () => void;
  onCheckGrammar?: () => void;
  onGenerateCreative?: () => void;
  hasVisualSuggestions?: boolean;
  loadingEnhance?: boolean;
  loadingImage?: boolean;
  loadingVideo?: boolean;
  loadingEnhanceImage?: boolean;
  loadingGrammar?: boolean;
  showAIFeatures?: boolean;
  galleryItems: Array<{
    file: File | null;
    preview: string;
    type: "image" | "video";
    id?: string;
    variations?: Record<string, { public_url: string }>;
  }>;
  setGalleryItems: (
    items: Array<{
      file: File | null;
      preview: string;
      type: "image" | "video";
      id?: string;
      variations?: Record<string, { public_url: string }>;
    }>,
  ) => void;
}

/**
 * Limites do upload de mídia — os mesmos do backend (posts/media/upload_image.py e
 * upload_video.py). Validar aqui evita que o usuário só descubra o problema ao salvar.
 */
const IMAGE_TYPES = ["image/jpeg", "image/png", "image/gif", "image/webp"];
const VIDEO_EXTENSIONS = [".mp4", ".mov", ".avi", ".mkv", ".webm"];
const MAX_IMAGE_MB = 10;
const MAX_VIDEO_MB = 200;
const MEDIA_ACCEPT = `${IMAGE_TYPES.join(",")},${VIDEO_EXTENSIONS.join(",")}`;

const formatMb = (bytes: number) =>
  (bytes / 1024 / 1024).toLocaleString("pt-BR", { maximumFractionDigits: 1 });

/** Mensagem do problema do arquivo, ou null se ele pode ser enviado. */
function validateMediaFile(
  file: File,
  t: (key: string, vars?: Record<string, string | number>) => string,
): string | null {
  const ext = file.name.slice(file.name.lastIndexOf(".")).toLowerCase();
  if (file.type.startsWith("image/")) {
    if (!IMAGE_TYPES.includes(file.type)) {
      return t("“{name}”: formato não suportado. Use JPG, PNG, GIF ou WEBP.", {
        name: file.name,
      });
    }
    if (file.size > MAX_IMAGE_MB * 1024 * 1024) {
      return t("“{name}” tem {size}MB; o máximo para imagens é {max}MB.", {
        name: file.name,
        size: formatMb(file.size),
        max: MAX_IMAGE_MB,
      });
    }
    return null;
  }
  if (file.type.startsWith("video/") || VIDEO_EXTENSIONS.includes(ext)) {
    if (!VIDEO_EXTENSIONS.includes(ext)) {
      return t(
        "“{name}”: formato não suportado. Use MP4, MOV, AVI, MKV ou WEBM.",
        {
          name: file.name,
        },
      );
    }
    if (file.size > MAX_VIDEO_MB * 1024 * 1024) {
      return t("“{name}” tem {size}MB; o máximo para vídeos é {max}MB.", {
        name: file.name,
        size: formatMb(file.size),
        max: MAX_VIDEO_MB,
      });
    }
    return null;
  }
  return t("“{name}”: envie uma imagem ou um vídeo.", { name: file.name });
}

const EditPostModal: React.FC<EditPostModalProps> = ({
  open,
  onClose,
  onConfirm,
  loading,
  editContent,
  setEditContent,
  editHashtags,
  setEditHashtags,
  editCallToAction,
  setEditCallToAction,
  editCallToActionUrl,
  setEditCallToActionUrl,
  postId,
  platform,
  onEnhanceText,
  onGenerateImage,
  onGenerateVideo,
  onUploadImage,
  onUploadVideo,
  onEnhanceImage,
  onVisualSuggestions,
  onCheckGrammar,
  onGenerateCreative,
  hasVisualSuggestions = false,
  loadingEnhance = false,
  loadingImage = false,
  loadingVideo = false,
  loadingEnhanceImage = false,
  loadingGrammar = false,
  showAIFeatures = false,
  galleryItems,
  setGalleryItems,
}) => {
  const { t } = useTranslation();
  // Arquivos recusados na seleção (tamanho/formato/limite), mostrados na galeria.
  const [mediaErrors, setMediaErrors] = useState<string[]>([]);
  const { activeContext } = useActiveContext();
  const [isSubmitting, setIsSubmitting] = useState(false);
  // Vira `true` na primeira tentativa de envio com campos obrigatórios
  // vazios — liga o destaque visual (borda vermelha) nesses campos.
  const [attemptedSubmit, setAttemptedSubmit] = useState(false);
  const [generateImage, setGenerateImage] = useState(false);
  const [generatingImage, setGeneratingImage] = useState(false);
  const [imagePrompt, setImagePrompt] = useState("");
  const [enhancingText, setEnhancingText] = useState(false);
  const [variationsModalOpen, setVariationsModalOpen] = useState(false);
  const [selectedItemForVariations, setSelectedItemForVariations] = useState<{
    variations: Record<string, { public_url: string }>;
    originalUrl: string;
  } | null>(null);
  const [activeTab, setActiveTab] = useState("media");

  // Hashtags como pills, sincronizadas com o campo `editHashtags` (string)
  // do pai — mesmo padrão visual do CreatePostModal, mas mantendo o
  // contrato de dados (string) já usado pelo resto do fluxo de edição.
  const [hashtagsArray, setHashtagsArray] = useState<string[]>([]);
  const [hashtagInput, setHashtagInput] = useState("");

  useEffect(() => {
    if (open) {
      const parsed = editHashtags
        .split(/\s+/)
        .map((tag) => tag.trim().replace(/^#+/, ""))
        .filter(Boolean);
      setHashtagsArray(parsed);
      setHashtagInput("");
      setActiveTab("media");
      setAttemptedSubmit(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const addHashtag = (tagInput: string) => {
    // Pode conter multiplas hashtags separadas por espaço ou vírgula se colado
    const tags = tagInput.split(/[\s,]+/).filter(Boolean);
    let newArray = [...hashtagsArray];
    let changed = false;

    tags.forEach((tag) => {
      const cleanedTag = tag.trim().replace(/^#+/, "");
      if (cleanedTag && !newArray.includes(cleanedTag)) {
        newArray.push(cleanedTag);
        changed = true;
      }
    });

    if (changed) {
      setHashtagsArray(newArray);
      setEditHashtags(newArray.join(" "));
    }
    setHashtagInput("");
  };

  const removeHashtag = (tagToRemove: string) => {
    const newArray = hashtagsArray.filter((tag) => tag !== tagToRemove);
    setHashtagsArray(newArray);
    setEditHashtags(newArray.join(" "));
  };

  const handleHashtagInputKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement>,
  ) => {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      if (hashtagInput.trim()) addHashtag(hashtagInput);
    }
  };

  const handleHashtagInputBlur = () => {
    if (hashtagInput.trim()) addHashtag(hashtagInput);
  };

  const captionLimit = useMemo(
    () =>
      getStrictestCaptionLimit(
        platform ? [platform] : Object.keys(POST_CAPTION_LIMITS),
      ),
    [platform],
  );

  const hashtagLimit = useMemo(
    () => getStrictestHashtagLimit(platform ? [platform] : []),
    [platform],
  );

  const previewAccountName =
    activeContext.clientCompany?.name ||
    activeContext.organization?.name ||
    t("Sua Empresa");
  const previewAccountLogo =
    activeContext.clientCompany?.logo_url ||
    activeContext.organization?.logo_url ||
    null;

  const handleEnhanceTextClick = async () => {
    if (!onEnhanceText) return;
    const confirmed = await showConfirm(
      t(
        "A IA vai reescrever sua legenda atual (e pode ajustar CTA e hashtags). O texto atual será substituído.",
      ),
      t("Melhorar com IA?"),
      t("Melhorar"),
      t("Cancelar"),
    );
    if (!confirmed) return;
    try {
      setEnhancingText(true);
      await onEnhanceText();
    } finally {
      setEnhancingText(false);
    }
  };

  const removeItem = (index: number) => {
    const newItems = [...galleryItems];
    newItems.splice(index, 1);
    setGalleryItems(newItems);
  };

  const openVariations = (index: number) => {
    const item = galleryItems[index];
    if (item.variations) {
      setSelectedItemForVariations({
        variations: item.variations,
        originalUrl: item.preview,
      });
      setVariationsModalOpen(true);
    }
  };

  const handleGenerateImagePreview = async () => {
    if (!imagePrompt.trim() || !postId) {
      if (!postId) {
        showToastError(t("Salve o post antes de gerar uma imagem com IA"));
      }
      return;
    }
    setGeneratingImage(true);
    try {
      const result = await postsService.generateImageAndWait(postId, {
        prompt: imagePrompt.trim(),
        provider: "dalle",
      });
      const imageInfo =
        (result.image_info as
          | {
              public_url?: string;
              variations?: Record<string, { public_url: string }>;
            }
          | undefined) ||
        (
          result.post as
            | {
                image_info?: {
                  public_url?: string;
                  variations?: Record<string, { public_url: string }>;
                };
              }
            | undefined
        )?.image_info;
      if (imageInfo?.public_url) {
        const imageUrl = imageInfo.public_url.startsWith("http")
          ? imageInfo.public_url
          : `${window.location.origin}${imageInfo.public_url}`;
        setGalleryItems([
          ...galleryItems,
          {
            file: new File([], "IA_Generated.png"),
            preview: imageUrl,
            type: "image",
            variations: imageInfo.variations,
          },
        ]);
        setGenerateImage(false);
      }
    } catch (e: unknown) {
      const err = e as Error;
      showToastError(err.message || t("Erro ao gerar imagem"));
    } finally {
      setGeneratingImage(false);
    }
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (isSubmitting) return;
    setAttemptedSubmit(true);
    if (!editContent.trim()) return;

    try {
      setIsSubmitting(true);
      await onConfirm();
    } catch (error) {
      console.error("Erro ao salvar:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={postId ? t("Editar Post #{postId}", { postId }) : t("Editar Post")}
      subtitle={t("Ajuste o conteúdo, hashtags e configurações do seu post")}
      icon={<Edit3 className="h-5 w-5" />}
      headerVariant="gradient"
      maxWidth="max-w-4xl"
      footer={
        <div className="flex justify-end gap-3 w-full">
          <Button
            type="button"
            variant="secondary"
            onClick={onClose}
            disabled={loading || isSubmitting}
            size="sm"
          >
            {t("Cancelar")}
          </Button>
          <Button
            type="submit"
            variant="primary"
            icon={
              loading || isSubmitting ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Edit3 className="w-4 h-4" />
              )
            }
            disabled={loading || isSubmitting || !editContent.trim()}
            form="edit-post-form"
            size="sm"
            className="shadow-lg shadow-purple-500/20"
          >
            {isSubmitting ? t("Salvando...") : t("Salvar Alterações")}
          </Button>
        </div>
      }
    >
      <form
        id="edit-post-form"
        onSubmit={handleSubmit}
        className="p-0.5 space-y-5"
      >
        {/* Seção 1 - Conteúdo */}
        <div className="space-y-3">
          <div className="flex items-center justify-between px-1">
            <div className="flex items-center gap-2">
              <label className="block text-[11px] font-bold uppercase tracking-wider text-gray-400">
                {t("Conteúdo do Post")}
              </label>
              <span className="text-red-400 font-bold">*</span>
            </div>
            <CharCounter
              current={editContent.length}
              recommended={captionLimit?.recommended}
              hard={captionLimit?.hard}
            />
          </div>
          <div className="relative">
            <Textarea
              label=""
              value={editContent}
              onChange={(e) => setEditContent(e.target.value)}
              required
              error={
                attemptedSubmit && !editContent.trim()
                  ? t("A legenda é obrigatória")
                  : undefined
              }
              placeholder={t("Escreva o conteúdo do seu post aqui...")}
              className={cn(
                "p-4 pb-12 rounded-2xl! focus:border-purple-500! text-sm bg-gray-50! focus:bg-white! placeholder:text-gray-400! min-h-[130px]! leading-relaxed shadow-inner",
                attemptedSubmit && !editContent.trim()
                  ? "border-red-300!"
                  : "border-purple-200!",
              )}
            />
            {onEnhanceText && (
              <div className="absolute bottom-3 right-3">
                <Button
                  type="button"
                  variant="primary"
                  size="sm"
                  onClick={handleEnhanceTextClick}
                  disabled={
                    enhancingText || loadingEnhance || !editContent.trim()
                  }
                  icon={
                    enhancingText || loadingEnhance ? (
                      <Loader2 className="h-3 w-3 animate-spin" />
                    ) : (
                      <Wand2 className="h-3 w-3" />
                    )
                  }
                  className="rounded-xl shadow-md shadow-purple-500/10"
                >
                  {enhancingText || loadingEnhance
                    ? t("Processando...")
                    : t("Melhorar com IA")}
                </Button>
              </div>
            )}
          </div>
        </div>

        {/* Hashtags, CTA e Link — sempre visíveis */}
        <div className="space-y-4">
          <div className="space-y-2">
            <div className="flex items-center justify-between px-1">
              <div className="flex items-center gap-2">
                <Hash className="w-3.5 h-3.5 text-purple-600" />
                <label className="block text-[11px] font-bold uppercase tracking-wider text-gray-400">
                  {t("Hashtags (opcional)")}
                </label>
              </div>
              {hashtagLimit !== undefined ? (
                <CharCounter
                  current={hashtagsArray.length}
                  hard={hashtagLimit}
                />
              ) : (
                <span className="text-[10px] font-bold text-indigo-600 bg-indigo-50 px-2 py-0.5 rounded-full border border-purple-200">
                  {t("{count} Ativas", { count: hashtagsArray.length })}
                </span>
              )}
            </div>

            {hashtagsArray.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-3 bg-white p-3 rounded-xl border border-purple-200 min-h-[48px]">
                {hashtagsArray.map((tag, index) => (
                  <span
                    key={index}
                    className="inline-flex items-center gap-1.5 pl-3 pr-1.5 py-1 rounded-full text-[11px] font-bold bg-indigo-50 text-indigo-700 border border-purple-200 group transition-all hover:bg-indigo-100"
                  >
                    #{tag}
                    <button
                      type="button"
                      onClick={() => removeHashtag(tag)}
                      className="text-indigo-300 hover:text-red-500 hover:bg-white rounded-full p-0.5 transition-all focus:outline-hidden"
                    >
                      <Plus className="h-3.5 w-3.5 rotate-45" />
                    </button>
                  </span>
                ))}
              </div>
            )}

            <Input
              type="text"
              value={hashtagInput}
              onChange={(e) => setHashtagInput(e.target.value)}
              onKeyDown={handleHashtagInputKeyDown}
              onBlur={handleHashtagInputBlur}
              placeholder={t("Digite e pressione Enter...")}
              inputSize="sm"
              className="shadow-xs"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="space-y-2">
              <div className="flex items-center gap-2 px-1">
                <MessageSquare className="w-3.5 h-3.5 text-purple-600" />
                <label className="block text-[11px] font-bold uppercase tracking-wider text-gray-400">
                  {t("Call to Action (opcional)")}
                </label>
              </div>
              <Input
                type="text"
                value={editCallToAction}
                onChange={(e) => setEditCallToAction(e.target.value)}
                placeholder={t("Ex: Saiba mais, Comente, Compartilhe...")}
                inputSize="sm"
                className="border-purple-200!"
              />
            </div>

            <div className="space-y-2">
              <div className="flex items-center gap-2 px-1">
                <Link className="w-3.5 h-3.5 text-purple-600" />
                <label className="block text-[11px] font-bold uppercase tracking-wider text-gray-400">
                  {t("Link do CTA (opcional)")}
                </label>
              </div>
              <Input
                type="url"
                value={editCallToActionUrl}
                onChange={(e) => setEditCallToActionUrl(e.target.value)}
                placeholder="https://exemplo.com/landing"
                inputSize="sm"
                className="border-purple-200!"
              />
            </div>
          </div>
        </div>

        {/* Tabs para Mídia e Pré-visualização */}
        <div className="w-full">
          <div
            role="group"
            aria-label={t("Alternar entre Mídia e Pré-visualização")}
            className="mb-3 sm:mb-4 flex w-full items-center gap-1 p-1 bg-gray-100 rounded-xl"
          >
            <button
              type="button"
              onClick={() => setActiveTab("media")}
              aria-pressed={activeTab === "media"}
              className={cn(
                "flex flex-1 items-center justify-center gap-1.5 px-4 py-2 rounded-lg text-xs font-bold uppercase tracking-wide transition-all",
                activeTab === "media"
                  ? "bg-purple-600 text-white shadow-xs"
                  : "text-gray-500 hover:text-gray-700",
              )}
            >
              <ImageIcon className="h-4 w-4" />
              {t("Mídia")}
            </button>
            <button
              type="button"
              onClick={() => setActiveTab("preview")}
              aria-pressed={activeTab === "preview"}
              className={cn(
                "flex flex-1 items-center justify-center gap-1.5 px-4 py-2 rounded-lg text-xs font-bold uppercase tracking-wide transition-all",
                activeTab === "preview"
                  ? "bg-purple-600 text-white shadow-xs"
                  : "text-gray-500 hover:text-gray-700",
              )}
            >
              <Eye className="h-4 w-4" />
              {t("Pré-visualização")}
            </button>
          </div>

          <Tabs
            value={activeTab}
            onValueChange={setActiveTab}
            className="w-full"
          >
            <TabsList className="hidden">
              <TabsTrigger value="media">{t("Mídia")}</TabsTrigger>
              <TabsTrigger value="preview">{t("Pré-visualização")}</TabsTrigger>
            </TabsList>

            <TabsContent
              value="preview"
              className="space-y-4 mt-4 focus:outline-hidden"
            >
              <div className="max-w-sm mx-auto bg-white border border-purple-200 rounded-2xl shadow-xs overflow-hidden">
                <div className="p-3 flex items-center gap-2.5 border-b border-gray-100">
                  {previewAccountLogo ? (
                    <img
                      src={previewAccountLogo}
                      alt=""
                      className="w-9 h-9 rounded-full object-cover shrink-0 border border-gray-100"
                    />
                  ) : (
                    <div className="w-9 h-9 rounded-full bg-linear-to-br from-purple-500 to-indigo-600 flex items-center justify-center text-white text-xs font-black shrink-0">
                      {previewAccountName.charAt(0).toUpperCase()}
                    </div>
                  )}
                  <div className="min-w-0 flex-1">
                    <p className="text-xs font-bold text-gray-900 truncate">
                      {previewAccountName}
                    </p>
                    <p className="text-[10px] text-gray-400">
                      {platform ? getPlatformName(platform) : t("Post")} •{" "}
                      {t("Agora")}
                    </p>
                  </div>
                </div>

                {galleryItems[0] && (
                  <div className="w-full aspect-square bg-gray-100">
                    {galleryItems[0].type === "image" ? (
                      <img
                        src={galleryItems[0].preview}
                        alt=""
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Video className="h-10 w-10 text-gray-300" />
                      </div>
                    )}
                  </div>
                )}

                <div className="p-3.5 space-y-2">
                  <p className="text-xs text-gray-800 whitespace-pre-wrap wrap-break-word leading-relaxed">
                    {editContent || (
                      <span className="italic text-gray-300">
                        {t("Sua legenda vai aparecer aqui...")}
                      </span>
                    )}
                  </p>
                  {hashtagsArray.length > 0 && (
                    <p className="text-xs text-blue-600 wrap-break-word">
                      {hashtagsArray.map((tag) => `#${tag}`).join(" ")}
                    </p>
                  )}
                  {editCallToAction && (
                    <div className="pt-2">
                      <span className="inline-block px-3 py-1.5 rounded-lg bg-purple-600 text-white text-[11px] font-bold">
                        {editCallToAction}
                      </span>
                    </div>
                  )}
                </div>
              </div>

              {galleryItems.length > 1 && (
                <p className="text-center text-[10px] text-gray-400 font-medium">
                  {t("+ {count} mídias no carrossel", {
                    count: galleryItems.length - 1,
                  })}
                </p>
              )}
            </TabsContent>

            <TabsContent
              value="media"
              className="space-y-4 mt-4 focus:outline-hidden"
            >
              {/* Galeria do Post */}
              <div className="space-y-4">
                <div className="flex items-center justify-between px-1">
                  <div className="flex items-center gap-2">
                    <ImageIcon className="w-3.5 h-3.5 text-purple-600" />
                    <label className="block text-[11px] font-bold uppercase tracking-wider text-gray-400">
                      {t("Galeria do Post")}
                    </label>
                  </div>
                  <span
                    className={cn(
                      "text-[10px] font-bold px-2 py-0.5 rounded-full border uppercase tracking-tighter shadow-xs",
                      galleryItems.length > 0
                        ? "bg-purple-600 text-white border-transparent"
                        : "bg-white text-purple-600 border-purple-200",
                    )}
                  >
                    {t("{count} / 10 Itens", { count: galleryItems.length })}
                  </span>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
                  {galleryItems.map((item, index) => (
                    <div
                      key={index}
                      className="relative aspect-square group overflow-hidden rounded-2xl border-2 border-purple-100 bg-white shadow-md transition-all hover:border-purple-400 hover:shadow-xl duration-300"
                    >
                      {item.type === "image" ? (
                        <img
                          src={item.preview}
                          className="w-full h-full object-cover transition-all duration-500 group-hover:scale-110"
                          alt={t("Item {number}", { number: index + 1 })}
                        />
                      ) : (
                        <div className="w-full h-full bg-indigo-900 flex flex-col items-center justify-center text-white/90 gap-1.5 p-2 text-center">
                          <Video className="h-6 w-6 opacity-80" />
                          <span className="text-[8px] font-extrabold uppercase tracking-widest opacity-60 truncate w-full px-1">
                            {item.file?.name || t("Vídeo")}
                          </span>
                        </div>
                      )}

                      <button
                        type="button"
                        onClick={() => removeItem(index)}
                        className="absolute top-1 right-1 p-1 bg-red-500/90 text-white rounded-lg opacity-0 group-hover:opacity-100 transition-all duration-300 shadow-lg hover:bg-red-600 scale-75 group-hover:scale-100"
                      >
                        <X className="h-3 w-3" />
                      </button>

                      {/* Badge de Variações */}
                      {item.variations && (
                        <button
                          type="button"
                          onClick={() => openVariations(index)}
                          className="absolute bottom-1 left-1 px-1.5 py-0.5 bg-green-500 hover:bg-green-600 text-white text-[8px] font-black uppercase rounded-sm shadow-lg flex items-center gap-1 transition-colors"
                        >
                          <Sparkles className="h-2 w-2" />
                          {t("Optimized")}
                        </button>
                      )}

                      <div className="absolute bottom-1 right-1 px-1.5 py-0.5 bg-black/60 backdrop-blur-xs rounded-md text-[9px] font-bold text-white">
                        #{index + 1}
                      </div>
                    </div>
                  ))}

                  {mediaErrors.length > 0 && (
                    <div
                      role="alert"
                      className="col-span-full flex items-start gap-2 rounded-xl border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-800"
                    >
                      <div className="flex-1 space-y-0.5">
                        {mediaErrors.map((message) => (
                          <p key={message}>{message}</p>
                        ))}
                      </div>
                      <button
                        type="button"
                        onClick={() => setMediaErrors([])}
                        className="shrink-0 font-semibold text-rose-700 hover:underline"
                      >
                        {t("Fechar")}
                      </button>
                    </div>
                  )}

                  {galleryItems.length < 10 && (
                    <>
                      <label className="cursor-pointer group flex flex-col items-center justify-center p-3 rounded-2xl border-2 border-dashed border-purple-200 bg-white/40 hover:border-purple-500 hover:bg-white transition-all aspect-square shadow-xs duration-300">
                        <div className="p-2 bg-purple-50 rounded-xl group-hover:bg-purple-600 transition-colors duration-300 group-hover:text-white text-purple-600 shadow-inner">
                          <Plus className="h-5 w-5" />
                        </div>
                        <span className="mt-2 text-[9px] font-bold text-gray-500 uppercase tracking-tighter group-hover:text-purple-600">
                          {t("Add Mídia")}
                        </span>
                        <input
                          type="file"
                          multiple
                          accept={MEDIA_ACCEPT}
                          onChange={async (e) => {
                            const input = e.target;
                            const files = Array.from(input.files || []);
                            input.value = ""; // permite escolher o mesmo arquivo de novo
                            const errors: string[] = [];
                            const free = 10 - galleryItems.length;
                            const accepted = files.filter((file) => {
                              const problem = validateMediaFile(file, t);
                              if (problem) errors.push(problem);
                              return !problem;
                            });
                            if (accepted.length > free) {
                              errors.push(
                                t(
                                  "Limite de 10 mídias por post: {n} arquivo(s) não foram adicionados.",
                                  { n: accepted.length - free },
                                ),
                              );
                            }
                            const toAdd = accepted.slice(0, Math.max(free, 0));
                            // Lê todas as prévias antes de atualizar: com vários arquivos de uma
                            // vez, atualizar um por um a partir da mesma lista guardava só o último.
                            const newItems = await Promise.all(
                              toAdd.map(
                                (file) =>
                                  new Promise<{
                                    file: File;
                                    preview: string;
                                    type: "image" | "video";
                                  }>((resolve) => {
                                    if (!file.type.startsWith("image/")) {
                                      resolve({
                                        file,
                                        preview: "",
                                        type: "video",
                                      });
                                      return;
                                    }
                                    const reader = new FileReader();
                                    reader.onloadend = () =>
                                      resolve({
                                        file,
                                        preview: reader.result as string,
                                        type: "image",
                                      });
                                    reader.readAsDataURL(file);
                                  }),
                              ),
                            );
                            setMediaErrors(errors);
                            if (newItems.length) {
                              setGalleryItems([...galleryItems, ...newItems]);
                            }
                          }}
                          className="hidden"
                        />
                      </label>

                      <button
                        type="button"
                        onClick={() => setGenerateImage(true)}
                        className="group flex flex-col items-center justify-center p-3 rounded-2xl border-2 border-dashed border-indigo-200 bg-white/40 hover:border-indigo-500 hover:bg-white transition-all aspect-square shadow-xs duration-300"
                      >
                        <div className="p-2 bg-indigo-50 rounded-xl group-hover:bg-indigo-600 transition-colors duration-300 group-hover:text-white text-indigo-600 shadow-inner">
                          <Sparkles className="h-5 w-5" />
                        </div>
                        <span className="mt-2 text-[9px] font-bold text-gray-500 uppercase tracking-tighter group-hover:text-indigo-600">
                          {t("Gerar IA")}
                        </span>
                      </button>
                    </>
                  )}
                </div>

                {/* Painel IA Gerativa no Edit */}
                {generateImage && (
                  <div className="mt-4 p-4 bg-white/95 backdrop-blur-md rounded-2xl border border-indigo-100 shadow-xl animate-fade-in">
                    {!generatingImage ? (
                      <div className="space-y-4">
                        <div className="flex items-center justify-between border-b border-indigo-50 pb-2">
                          <div className="flex items-center gap-2">
                            <Sparkles className="h-3.5 w-3.5 text-indigo-500" />
                            <span className="text-[10px] font-extrabold text-indigo-700 uppercase tracking-wider">
                              {t("Novo Visual IA")}
                            </span>
                          </div>
                          <button
                            onClick={() => setGenerateImage(false)}
                            className="text-gray-400 hover:text-red-500 transition-colors"
                          >
                            <X className="h-4 w-4" />
                          </button>
                        </div>
                        <Input
                          placeholder={t(
                            "Sugira um cenário para esta imagem...",
                          )}
                          value={imagePrompt}
                          onChange={(e) => setImagePrompt(e.target.value)}
                          inputSize="sm"
                          className="bg-gray-50/50!"
                        />
                        <div className="flex gap-2">
                          <Button
                            variant="primary"
                            size="sm"
                            className="flex-1 rounded-xl bg-indigo-600"
                            onClick={handleGenerateImagePreview}
                            disabled={!imagePrompt.trim()}
                          >
                            {t("Gerar e Incluir")}
                          </Button>
                          <Button
                            variant="secondary"
                            size="sm"
                            className="flex-1 rounded-xl"
                            onClick={() => setGenerateImage(false)}
                          >
                            {t("Cancelar")}
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <div className="py-8 flex flex-col items-center justify-center space-y-4 animate-pulse text-center">
                        <Loader2 className="h-8 w-8 animate-spin text-indigo-600" />
                        <p className="text-[10px] font-bold text-indigo-700 uppercase tracking-widest">
                          {t("A IA está criando sua nova arte...")}
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </TabsContent>
          </Tabs>
        </div>

        {/* Seção de Features de IA para Posts Aprovados */}
        {showAIFeatures && (
          <div className="pt-4 mt-2 border-t border-purple-200 flex flex-col gap-4">
            <div className="flex items-center justify-between px-1">
              <div className="flex items-center gap-2">
                <Zap className="w-4 h-4 text-purple-600" />
                <h3 className="text-xs font-bold uppercase tracking-widest text-gray-800">
                  {t("Ferramentas de IA")}
                </h3>
              </div>
              <span className="text-[10px] font-bold text-indigo-600 bg-indigo-50 px-2 py-0.5 rounded-full border border-purple-200">
                {t("Otimização Automatizada")}
              </span>
            </div>

            <div className="bg-linear-to-br from-purple-50/50 to-indigo-50/50 rounded-2xl p-5 border border-purple-200/50 relative overflow-hidden group">
              <div className="absolute top-0 right-0 p-3 opacity-10 group-hover:opacity-20 transition-opacity">
                <Sparkles className="h-12 w-12 text-purple-600" />
              </div>

              <p className="relative z-10 text-[11px] text-gray-500 font-bold uppercase tracking-tighter mb-4 px-1">
                {t("O que você deseja fazer agora?")}
              </p>

              <div className="relative z-10 grid grid-cols-2 md:grid-cols-4 gap-2.5">
                {onCheckGrammar && (
                  <button
                    type="button"
                    onClick={onCheckGrammar}
                    disabled={loadingGrammar}
                    className={cn(
                      "flex flex-col items-center justify-center gap-2 p-3 rounded-xl border-2 transition-all duration-300 group/btn",
                      "bg-white border-transparent shadow-xs hover:shadow-md hover:border-emerald-200 active:scale-95",
                      loadingGrammar && "opacity-60 cursor-not-allowed",
                    )}
                    title={t("Verificar e corrigir erros de gramática")}
                  >
                    <div className="p-2 bg-emerald-50 text-emerald-600 rounded-lg group-hover/btn:bg-emerald-600 group-hover/btn:text-white transition-colors">
                      {loadingGrammar ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <CheckCircle2 className="h-4 w-4" />
                      )}
                    </div>
                    <span className="text-[10px] font-bold text-gray-600 group-hover/btn:text-emerald-700 transition-colors">
                      {t("Revisar")}
                    </span>
                  </button>
                )}

                {onEnhanceImage && (
                  <button
                    type="button"
                    onClick={onEnhanceImage}
                    disabled={loadingEnhanceImage}
                    className={cn(
                      "flex flex-col items-center justify-center gap-2 p-3 rounded-xl border-2 transition-all duration-300 group/btn",
                      "bg-white border-transparent shadow-xs hover:shadow-md hover:border-orange-200 active:scale-95",
                      loadingEnhanceImage && "opacity-60 cursor-not-allowed",
                    )}
                    title={t("Melhorar qualidade da imagem e remover fundo")}
                  >
                    <div className="p-2 bg-orange-50 text-orange-600 rounded-lg group-hover/btn:bg-orange-600 group-hover/btn:text-white transition-colors">
                      {loadingEnhanceImage ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Palette className="h-4 w-4" />
                      )}
                    </div>
                    <span className="text-[10px] font-bold text-gray-600 group-hover/btn:text-orange-700 transition-colors">
                      {t("Melhorar Imagem")}
                    </span>
                  </button>
                )}

                {onGenerateCreative && (
                  <button
                    type="button"
                    onClick={onGenerateCreative}
                    className={cn(
                      "flex flex-col items-center justify-center gap-2 p-3 rounded-xl border-2 transition-all duration-300 group/btn",
                      "bg-white border-transparent shadow-xs hover:shadow-md hover:border-indigo-200 active:scale-95",
                    )}
                    title={t(
                      "Criar imagem criativa com texto sobreposto para campanha",
                    )}
                  >
                    <div className="p-2 bg-indigo-50 text-indigo-600 rounded-lg group-hover/btn:bg-indigo-600 group-hover/btn:text-white transition-colors">
                      <Palette className="h-4 w-4" />
                    </div>
                    <span className="text-[10px] font-bold text-gray-600 group-hover/btn:text-indigo-700 transition-colors">
                      {t("Criativo")}
                    </span>
                  </button>
                )}

                {onVisualSuggestions && (
                  <button
                    type="button"
                    onClick={onVisualSuggestions}
                    className={cn(
                      "flex flex-col items-center justify-center gap-2 p-3 rounded-xl border-2 transition-all duration-300 group/btn",
                      "bg-white border-transparent shadow-xs hover:shadow-md hover:border-teal-200 active:scale-95",
                    )}
                    title={
                      hasVisualSuggestions
                        ? t("Sugestões visuais já geradas")
                        : t("Obter sugestões visuais")
                    }
                  >
                    <div className="p-2 bg-teal-50 text-teal-600 rounded-lg group-hover/btn:bg-teal-600 group-hover/btn:text-white transition-colors relative">
                      <Eye className="h-4 w-4" />
                      {hasVisualSuggestions && (
                        <span className="absolute -top-1 -right-1 w-2.5 h-2.5 bg-emerald-500 border-2 border-white rounded-full" />
                      )}
                    </div>
                    <span className="text-[10px] font-bold text-gray-600 group-hover/btn:text-teal-700 transition-colors">
                      {t("Sugestões")}
                    </span>
                  </button>
                )}

                {(onUploadImage || onUploadVideo) && (
                  <div className="flex gap-2">
                    {onUploadImage && (
                      <button
                        type="button"
                        onClick={onUploadImage}
                        className={cn(
                          "flex-1 flex flex-col items-center justify-center gap-2 p-3 rounded-xl border-2 transition-all duration-300 group/btn",
                          "bg-white border-transparent shadow-xs hover:shadow-md hover:border-blue-200 active:scale-95",
                        )}
                      >
                        <div className="p-2 bg-blue-50 text-blue-600 rounded-lg group-hover/btn:bg-blue-600 group-hover/btn:text-white transition-colors">
                          <ImageIcon className="h-4 w-4" />
                        </div>
                        <span className="text-[10px] font-bold text-gray-600 group-hover/btn:text-blue-700 transition-colors">
                          {t("Enviar Imagem")}
                        </span>
                      </button>
                    )}
                    {onUploadVideo && (
                      <button
                        type="button"
                        onClick={onUploadVideo}
                        className={cn(
                          "flex-1 flex flex-col items-center justify-center gap-2 p-3 rounded-xl border-2 transition-all duration-300 group/btn",
                          "bg-white border-transparent shadow-xs hover:shadow-md hover:border-purple-200 active:scale-95",
                        )}
                      >
                        <div className="p-2 bg-purple-50 text-purple-600 rounded-lg group-hover/btn:bg-purple-600 group-hover/btn:text-white transition-colors">
                          <Video className="h-4 w-4" />
                        </div>
                        <span className="text-[10px] font-bold text-gray-600 group-hover/btn:text-purple-700 transition-colors">
                          {t("Enviar Vídeo")}
                        </span>
                      </button>
                    )}
                  </div>
                )}

                {(onGenerateImage || onGenerateVideo) && (
                  <button
                    type="button"
                    onClick={onGenerateImage || onGenerateVideo}
                    disabled={loadingImage || loadingVideo}
                    className={cn(
                      "flex flex-col items-center justify-center gap-2 p-3 rounded-xl border-2 transition-all duration-300 group/btn",
                      "bg-indigo-600 border-transparent shadow-lg hover:shadow-indigo-500/20 active:scale-95",
                      (loadingImage || loadingVideo) &&
                        "opacity-60 cursor-not-allowed",
                    )}
                  >
                    <div className="p-2 bg-white/20 text-white rounded-lg group-hover/btn:bg-white group-hover/btn:text-indigo-600 transition-colors">
                      {loadingImage || loadingVideo ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Wand2 className="h-4 w-4" />
                      )}
                    </div>
                    <span className="text-[10px] font-bold text-white">
                      {t("Gerar Mídia")}
                    </span>
                  </button>
                )}
              </div>
            </div>
          </div>
        )}
      </form>

      {selectedItemForVariations && (
        <SocialVariationsModal
          open={variationsModalOpen}
          onClose={() => setVariationsModalOpen(false)}
          variations={selectedItemForVariations.variations}
          originalUrl={selectedItemForVariations.originalUrl}
        />
      )}
    </Modal>
  );
};

export default EditPostModal;
